import { DEFAULT_COIN_LABEL } from './coin-label';

/**
 * Friendly copy for the checkout / release error codes the API returns.
 *
 * Lives here rather than in `pages/pos.tsx` because the releasing queue needs
 * it too, and a page module that exports a non-component breaks Fast Refresh
 * for the whole file.
 *
 * `coinLabel` is the tenant's name for the coin wallet (`useCoinLabel()`), so a
 * renamed tenant reads "insufficient UPCOIN balance". It defaults because only
 * the checkout can raise a coin code — the releasing caller never sees one and
 * has no reason to hold the label.
 */
export function prettyError(code: string, coinLabel: string = DEFAULT_COIN_LABEL): string {
  switch (code) {
    case 'out_of_stock':
      return 'Out of stock. Refresh the product list.';
    case 'insufficient_payment':
      return 'Tendered amount is less than the total.';
    case 'period_closed':
      return 'The current pay period is closed. Ask admin to open the next period.';
    case 'branch_scope_violation':
      return 'You are not authorized for this branch.';
    case 'tendered_must_equal_total_for_non_cash':
      return 'Card / wallet payment must equal the total.';
    case 'payment_method_not_allowed_for_role':
      return 'Your role can not accept this payment method.';
    case 'kit_code_already_used':
      return 'Cannot void: a kit code from this order has already been redeemed.';
    case 'order_not_releasable':
      return 'This order is not awaiting release.';
    case 'order_already_released':
      return 'This order was already released.';
    case 'slot_required_for_non_cash':
      return `The member needs an active slot to pay with GC / ${coinLabel}.`;
    case 'insufficient_gc':
      return 'Member has insufficient GC balance.';
    case 'insufficient_coin':
      return `Member has insufficient ${coinLabel} balance.`;
    case 'product_not_purchasable_in_gc':
      return 'An item is not sold in GC.';
    case 'product_not_purchasable_in_coin':
      return `An item is not sold in ${coinLabel}.`;
    case 'unknown_custom_fee_type':
      // Covers both "never existed" and "an admin just retired it" — the same
      // answer either way to the cashier standing at the till.
      return 'That custom fee is no longer available. Refresh and pick another.';
    case 'duplicate_custom_fee_type':
      return 'The same custom fee was added twice. Remove one.';
    case 'custom_fee_not_allowed_for_non_cash':
      return `Custom fees are cash only — remove them to pay with GC / ${coinLabel}.`;
    case 'custom_fee_negative':
      return 'A fee cannot be negative. Use the cashier discount to reduce a sale.';
    default:
      return code;
  }
}
